"use client"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Job, JobEmailImport } from "@/types/jobs"
import { useRouter } from "next/navigation"
import { ImportJobResult } from "../../types"
import { SubscriberImportSummary } from "./SubscriberImportSummary"
import { PostImportSummary } from "./PostImportSummary"

// Union type to support both job types
type JobType = Job | JobEmailImport | ImportJobResult | null

interface ImportCompleteModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  job: JobType
  type: "subscribers" | "posts"
}

/**
 * Modal displayed after an import operation completes
 * Shows different content based on import type (subscribers or posts)
 */
export function ImportCompleteModal({
  open,
  onOpenChange,
  job,
  type,
}: ImportCompleteModalProps) {
  const router = useRouter()

  if (!job) return null

  // For subscriber imports
  const isEmailImportJob =
    type === "subscribers" && (job as Job)?.type === "IMPORT_EMAILS"
  const emailImportJob = job as JobEmailImport
  const hasPartialSuccess =
    type === "subscribers" && (job as Job)?.errorCode === "PARTIAL_SUCCESS"

  // For subscriber import success count
  const subscriberSuccessCount =
    isEmailImportJob && emailImportJob.countSuccess !== undefined
      ? emailImportJob.countSuccess
      : (job as Job)?.successMessage
        ? parseInt((job as Job).successMessage || "0", 10)
        : 0

  // For post imports
  const postsJob = job as ImportJobResult

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[462px]">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold leading-[18px] tracking-[-0.025em]">
            Import complete!
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* For subscriber imports */}
          {type === "subscribers" && (
            <SubscriberImportSummary
              job={job as Job}
              subscriberSuccessCount={subscriberSuccessCount}
              hasPartialSuccess={hasPartialSuccess}
            />
          )}

          {/* For post imports */}
          {type === "posts" && <PostImportSummary postsJob={postsJob} />}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            onClick={() => {
              onOpenChange(false)
              router.push(
                type === "subscribers" ? "/subscribers" : "/dashboard"
              )
            }}
          >
            View {type}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
